import React, { useState } from 'react';
import {
  FlatList, LayoutAnimation, Image, Pressable, Modal, TouchableOpacity,
  Dimensions, StyleSheet,
} from 'react-native';
import {
  Card, Text, Chip, IconButton,
} from 'react-native-paper';
import { View } from 'native-base';

const fullScreenStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: Dimensions.get('window').width,
    height: Dimensions.get('window').height,
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: Dimensions.get('window').width * 0.95,
    height: Dimensions.get('window').height * 0.8,
  },
  closeButton: {
    position: 'absolute',
    top: 40,
    right: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 25,
  },
});

function RentalsTab({ user }) {
  const [expandedEquipments, setExpandedEquipments] = useState({});
  const [expandedDocumentation, setExpandedDocumentation] = useState({});
  const [fullScreenImageUri, setFullScreenImageUri] = useState(null);

  const sortedRentals = [...user.rentals].sort(
    (a, b) => new Date(b.rentalDate) - new Date(a.rentalDate),
  );

  const toggleEquipments = (rentalId) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedEquipments((prev) => ({
      ...prev,
      [rentalId]: !prev[rentalId],
    }));
  };

  const toggleDocumentation = (rentalId) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedDocumentation((prev) => ({
      ...prev,
      [rentalId]: !prev[rentalId],
    }));
  };

  const hasDocumentation = (item) => (
    item.rentalComment || item.rentalPicture || item.returnComment || item.returnPicture
  );

  const renderItem = ({ item }) => {
    const isEnCours = !item.returnDate;
    const cardStyle = {
      marginVertical: 8,
      backgroundColor: isEnCours ? '#e6ffe6' : 'white',
    };
    const isEquipmentsExpanded = expandedEquipments[item.id];
    const isDocumentationExpanded = expandedDocumentation[item.id];
    const hasDoc = hasDocumentation(item);
    const equipmentCount = item.bag.equipments.length;

    return (
      <Card style={cardStyle}>
        <Card.Title
          title={`Location du ${new Date(item.rentalDate).toLocaleDateString()}`}
          subtitle={isEnCours ? 'En cours' : `Retourné le ${new Date(item.returnDate).toLocaleDateString()}`}
          right={() => (
            <Text variant="titleMedium" style={{ marginRight: 8 }}>
              {item.bag.name}
            </Text>
          )}
        />
        <Card.Content>
          {/* Collapsible Equipment Section */}
          <Pressable
            onPress={() => equipmentCount > 0 && toggleEquipments(item.id)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Text variant="titleMedium">
              Équipements
              {equipmentCount > 0 ? ` (${equipmentCount})` : ''}
            </Text>
            {equipmentCount > 0 && (
              <IconButton
                icon={isEquipmentsExpanded ? 'chevron-up' : 'chevron-down'}
                size={20}
                onPress={() => toggleEquipments(item.id)}
              />
            )}
          </Pressable>

          {isEquipmentsExpanded && equipmentCount > 0 && (
            <View style={{ marginTop: 5 }}>
              {item.bag.equipments.map((eq) => (
                <Chip key={eq.equipment.id} style={{ margin: 2 }}>
                  {`${eq.equipment.name} - Taille: ${eq.equipment.size}`}
                </Chip>
              ))}
            </View>
          )}

          {equipmentCount === 0 && (
            <Text style={{ color: '#666', marginLeft: 10 }}>Aucun équipement</Text>
          )}

          {/* Collapsible Documentation Section */}
          {hasDoc && (
            <>
              <Pressable
                onPress={() => toggleDocumentation(item.id)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 10,
                  borderTopWidth: 1,
                  borderTopColor: '#e0e0e0',
                  paddingTop: 10,
                }}
              >
                <Text variant="titleMedium">Commentaires</Text>
                <IconButton
                  icon={isDocumentationExpanded ? 'chevron-up' : 'chevron-down'}
                  size={20}
                  onPress={() => toggleDocumentation(item.id)}
                />
              </Pressable>

              {isDocumentationExpanded && (
                <View style={{ marginTop: 5 }}>
                  {/* Rental documentation */}
                  {(item.rentalComment || item.rentalPicture) && (
                    <View style={{ marginBottom: 10 }}>
                      <Text style={{ fontWeight: '600', color: '#4CAF50', marginBottom: 5 }}>
                        📦 À la location:
                      </Text>
                      {item.rentalComment && (
                        <Text style={{ marginLeft: 10, fontStyle: 'italic', color: '#666' }}>
                          {item.rentalComment}
                        </Text>
                      )}
                      {item.rentalPicture && (
                        <Pressable
                          onPress={() => setFullScreenImageUri(
                            `data:image/jpeg;base64,${item.rentalPicture}`,
                          )}
                        >
                          <Image
                            source={{ uri: `data:image/jpeg;base64,${item.rentalPicture}` }}
                            style={{
                              width: 150,
                              height: 150,
                              marginLeft: 10,
                              marginTop: 5,
                              borderRadius: 8,
                            }}
                          />
                        </Pressable>
                      )}
                    </View>
                  )}

                  {/* Return documentation */}
                  {(item.returnComment || item.returnPicture) && (
                    <View>
                      <Text style={{ fontWeight: '600', color: '#2196F3', marginBottom: 5 }}>
                        📥 Au retour:
                      </Text>
                      {item.returnComment && (
                        <Text style={{ marginLeft: 10, fontStyle: 'italic', color: '#666' }}>
                          {item.returnComment}
                        </Text>
                      )}
                      {item.returnPicture && (
                        <Pressable
                          onPress={() => setFullScreenImageUri(
                            `data:image/jpeg;base64,${item.returnPicture}`,
                          )}
                        >
                          <Image
                            source={{ uri: `data:image/jpeg;base64,${item.returnPicture}` }}
                            style={{
                              width: 150,
                              height: 150,
                              marginLeft: 10,
                              marginTop: 5,
                              borderRadius: 8,
                            }}
                          />
                        </Pressable>
                      )}
                    </View>
                  )}
                </View>
              )}
            </>
          )}
        </Card.Content>
      </Card>
    );
  };

  return (
    <View style={{ flex: 1, padding: 16 }}>
      <FlatList
        data={sortedRentals}
        renderItem={renderItem}
        keyExtractor={(item) => item.id.toString()}
      />

      {/* Full-screen image modal */}
      <Modal
        visible={!!fullScreenImageUri}
        transparent
        animationType="fade"
        onRequestClose={() => setFullScreenImageUri(null)}
      >
        <TouchableOpacity
          style={fullScreenStyles.overlay}
          activeOpacity={1}
          onPress={() => setFullScreenImageUri(null)}
        >
          <View style={fullScreenStyles.container}>
            <Image
              source={{ uri: fullScreenImageUri }}
              style={fullScreenStyles.image}
              resizeMode="contain"
            />
            <TouchableOpacity
              style={fullScreenStyles.closeButton}
              onPress={() => setFullScreenImageUri(null)}
            >
              <IconButton icon="close" iconColor="white" size={30} />
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

export default RentalsTab;
